// Thermal-data loader + per-face vertex-color application.
//
// face_temp.f32 is float32[N] where N = face_count.
// The mesh is non-indexed (per BACKEND_HANDOFF), so each face has 3
// unique vertices. To color per-face, we write the same color to all
// 3 vertices.
//
// We use 4-component vertex colors (RGBA): RGB carries the Lava
// palette mapping, A carries a "has thermal coverage" flag (1.0 for
// faces with finite temp, 0.0 for NaN faces). The shader's mix factor
// multiplies by vColor.a so NaN faces always show the underlying RGB
// texture regardless of slider position.

import * as THREE from "three";
import { lavaColor } from "./colormap";

export interface ThermalData {
  /** float32[N] per-face mean apparent temperature (°C). NaN where no coverage. */
  temp: Float32Array;
  /** Min displayed (p01 from face_meta — keeps outliers from squashing the ramp). */
  rangeMin: number;
  /** Max displayed (p99 from face_meta). */
  rangeMax: number;
  /** True absolute min (meta.temp.min) — for the "Range" readout. */
  absMin: number;
  /** True absolute max. */
  absMax: number;
  /** Number of faces with finite temp. */
  validCount: number;
  /** Total face count. */
  faceCount: number;
  /** Raw face_meta.json — kept so callers can pull per-radius delta stats. */
  rawMeta: FaceMeta;
}

interface DeltaRadiusEntry {
  radius_m: number;
  file: string;
  default?: boolean;
  p01?: number;
  p99?: number;
  mean?: number;
  std?: number;
  valid_count?: number;
}

interface FaceMeta {
  face_count?: number;
  faces_with_thermal?: number;
  coverage_pct?: number;
  total_observations?: number;
  temp?: { min?: number; max?: number; mean?: number; std?: number; p01?: number; p99?: number };
  delta_radii?: DeltaRadiusEntry[];
  [k: string]: unknown;
}

export interface MeshMetaInfo {
  /** UTM offset applied at GLB build time (centroid → 0). Add this
   *  to a mesh-local Z-up point to recover UTM coordinates. */
  utm_center: [number, number, number];
  utm_extent: [number, number, number];
  face_count: number;
  vertex_count: number;
  source_obj?: string;
  transform_applied?: string;
  textured?: boolean;
}

/** Fetch mesh_meta.json — UTM offset + counts. */
export async function fetchMeshMeta(base: string): Promise<MeshMetaInfo> {
  const r = await fetch(`${base}mesh_meta.json`);
  if (!r.ok) throw new Error(`mesh_meta.json: ${r.status} ${r.statusText}`);
  return (await r.json()) as MeshMetaInfo;
}

/** Fetch low_e_faces.u32 — sorted uint32 face IDs flagged by the
 *  operator as low-emissivity. Returns a Set for O(1) membership.
 *  Resolves to an empty Set when the file isn't shipped (no
 *  classification sidecar). */
export async function fetchLowEmissivitySet(
  base: string
): Promise<Set<number>> {
  try {
    const r = await fetch(`${base}low_e_faces.u32`);
    if (!r.ok) return new Set();
    const buf = await r.arrayBuffer();
    if (buf.byteLength === 0) return new Set();
    const arr = new Uint32Array(buf);
    return new Set(arr);
  } catch {
    return new Set();
  }
}

/** Fetch face_truth_residual.f32 — signed per-face residual (°C)
 *  against SDK-decoded R-JPEG ground truth. Written by the accuracy
 *  test. NaN where no R-JPEG saw the face (video-only coverage).
 *  Resolves to null when the file isn't present (accuracy test
 *  wasn't run for this pass). */
export async function fetchTruthResidual(
  base: string
): Promise<Float32Array | null> {
  try {
    const r = await fetch(`${base}face_truth_residual.f32`);
    if (!r.ok) return null;
    const buf = await r.arrayBuffer();
    if (buf.byteLength === 0) return null;
    return new Float32Array(buf);
  } catch {
    return null;
  }
}

const TOLERANCE_WITHIN_RGB: [number, number, number] = [
  0x5a / 255,
  0x7a / 255,
  0x5e / 255,
]; // sage — within tolerance
const TOLERANCE_OVER_RGB: [number, number, number] = [
  0xc4 / 255,
  0x7a / 255,
  0x2a / 255,
]; // copper — outside tolerance

/** Build a non-indexed RGBA vertex-color BufferAttribute from a per-face
 *  signed residual array, classifying each face by |residual| against a
 *  tolerance threshold. Sage for within, copper for outside, NaN faces
 *  fall through (alpha=0). The visual is binary — buyers want to see
 *  "trust this region or not", not a continuous gradient. */
export function buildToleranceColorAttribute(
  residuals: Float32Array,
  thresholdC: number
): THREE.BufferAttribute {
  const nFaces = residuals.length;
  const colors = new Float32Array(nFaces * 3 * 4);

  for (let f = 0; f < nFaces; f++) {
    const r = residuals[f];
    let cr = 0,
      cg = 0,
      cb = 0,
      a = 0;
    if (Number.isFinite(r)) {
      const within = Math.abs(r) <= thresholdC;
      const rgb = within ? TOLERANCE_WITHIN_RGB : TOLERANCE_OVER_RGB;
      cr = rgb[0];
      cg = rgb[1];
      cb = rgb[2];
      a = 1;
    }
    for (let v = 0; v < 3; v++) {
      const i = (f * 3 + v) * 4;
      colors[i] = cr;
      colors[i + 1] = cg;
      colors[i + 2] = cb;
      colors[i + 3] = a;
    }
  }

  return new THREE.BufferAttribute(colors, 4);
}

export interface ToleranceSummary {
  /** Faces with finite residual data. */
  validCount: number;
  /** Faces whose |residual| is within the threshold. */
  withinCount: number;
  /** withinCount / validCount in [0, 1]. NaN if validCount = 0. */
  fraction: number;
  /** Threshold the summary was computed at. */
  thresholdC: number;
}

export function summarizeTolerance(
  residuals: Float32Array,
  thresholdC: number
): ToleranceSummary {
  let validCount = 0;
  let withinCount = 0;
  for (let i = 0; i < residuals.length; i++) {
    const r = residuals[i];
    if (Number.isFinite(r)) {
      validCount++;
      if (Math.abs(r) <= thresholdC) withinCount++;
    }
  }
  const fraction = validCount > 0 ? withinCount / validCount : NaN;
  return { validCount, withinCount, fraction, thresholdC };
}

export interface DeltaData {
  /** float32[N] per-face local-Δ from face_delta_r{NNN}.f32. */
  values: Float32Array;
  rangeMin: number;
  rangeMax: number;
  validCount: number;
  radiusM: number;
}

/** Fetch face_temp.f32 + face_meta.json from a per-pass bundle base. */
export async function fetchThermalData(base: string): Promise<ThermalData> {
  const [tempBuf, metaJson] = await Promise.all([
    fetch(`${base}face_temp.f32`).then((r) => {
      if (!r.ok) throw new Error(`face_temp.f32: ${r.status} ${r.statusText}`);
      return r.arrayBuffer();
    }),
    fetch(`${base}face_meta.json`).then((r) => {
      if (!r.ok) throw new Error(`face_meta.json: ${r.status} ${r.statusText}`);
      return r.json();
    }),
  ]);

  const temp = new Float32Array(tempBuf);
  // face_meta.json stats: { min, max, mean, std, p01, p99 }
  const t = metaJson.temp ?? {};
  const rangeMin =
    typeof t.p01 === "number"
      ? t.p01
      : typeof t.min === "number"
      ? t.min
      : 0;
  const rangeMax =
    typeof t.p99 === "number"
      ? t.p99
      : typeof t.max === "number"
      ? t.max
      : 1;
  const absMin = typeof t.min === "number" ? t.min : rangeMin;
  const absMax = typeof t.max === "number" ? t.max : rangeMax;

  let validCount = 0;
  for (let i = 0; i < temp.length; i++) if (Number.isFinite(temp[i])) validCount++;

  return {
    temp,
    rangeMin,
    rangeMax,
    absMin,
    absMax,
    validCount,
    faceCount: temp.length,
    rawMeta: metaJson as FaceMeta,
  };
}

/** Symmetric ±p99 from face_meta — the typical range, where 98% of
 *  the deltas live. Used as the slider bound + palette anchor so
 *  every slider position acts on real data. Outliers beyond ±p99
 *  fall through to the RGB texture by default. */
export function deltaTypicalRange(
  rawMeta: FaceMeta,
  radiusM: number
): { lo: number; hi: number } {
  const entry = rawMeta.delta_radii?.find(
    (r) => Math.abs(r.radius_m - radiusM) < 1e-6
  );
  if (!entry) return { lo: -0.5, hi: 0.5 };
  const sym = Math.max(
    Math.abs(entry.p01 ?? 0),
    Math.abs(entry.p99 ?? 0),
    0.1
  );
  return { lo: -sym, hi: sym };
}

/** Fetch one face_delta_r{NNN}.f32 and look up its stats from face_meta.
 *  Cached per (base, radius) so repeated toggles don't refetch. */
const _deltaCache = new Map<string, Promise<DeltaData>>();

export function fetchDeltaData(
  base: string,
  radiusM: number,
  rawMeta: FaceMeta
): Promise<DeltaData> {
  const key = `${base}__${radiusM}`;
  const cached = _deltaCache.get(key);
  if (cached) return cached;

  const entry = rawMeta.delta_radii?.find(
    (r) => Math.abs(r.radius_m - radiusM) < 1e-6
  );
  if (!entry) {
    return Promise.reject(
      new Error(`No delta_radii entry for radius ${radiusM} in face_meta.json`)
    );
  }

  const promise = fetch(`${base}${entry.file}`)
    .then((r) => {
      if (!r.ok) throw new Error(`${entry.file}: ${r.status} ${r.statusText}`);
      return r.arrayBuffer();
    })
    .then((buf) => {
      const values = new Float32Array(buf);
      let validCount = 0;
      for (let i = 0; i < values.length; i++) {
        if (Number.isFinite(values[i])) validCount++;
      }
      // Use the face_meta-supplied p01/p99 for symmetric-ish range,
      // but symmetrize around 0 for relative deltas (cold and hot
      // anomalies should be visually equivalent magnitudes).
      const p01 = typeof entry.p01 === "number" ? entry.p01 : -1;
      const p99 = typeof entry.p99 === "number" ? entry.p99 : 1;
      const sym = Math.max(Math.abs(p01), Math.abs(p99));
      return {
        values,
        rangeMin: -sym,
        rangeMax: sym,
        validCount,
        radiusM,
      };
    });

  _deltaCache.set(key, promise);
  return promise;
}

/** Build a non-indexed RGBA vertex-color BufferAttribute from a per-face
 *  scalar array.
 *
 *  Palette anchors = slider bounds [paletteMin, paletteMax]. The Lava
 *  ramp stretches across this range — every slider position maps to
 *  data that actually exists, no dead zones.
 *
 *  [bandLo, bandHi] is a sub-band filter: surfaces inside the band
 *  show with their palette colour; surfaces outside (including any
 *  outliers beyond the palette range) fall to alpha=0, RGB texture
 *  shows through. Default band = slider bounds, so toggle-on shows
 *  every typical-range surface coloured.
 *
 *  Outliers beyond the palette range are NOT clamped to ramp ends —
 *  they fall to RGB. Better to lose 1% than mislead a buyer with an
 *  artificial "yellow" that's actually a clamped 12° on a palette
 *  that ends at 9°. */
export function buildFaceColorAttribute(
  values: Float32Array,
  paletteMin: number,
  paletteMax: number,
  bandLo: number,
  bandHi: number
): THREE.BufferAttribute {
  const nFaces = values.length;
  const palette = paletteMax - paletteMin || 1;
  const colors = new Float32Array(nFaces * 3 * 4);

  for (let f = 0; f < nFaces; f++) {
    const t = values[f];
    let r = 0,
      g = 0,
      b = 0,
      a = 0;
    if (
      Number.isFinite(t) &&
      t >= paletteMin &&
      t <= paletteMax &&
      t >= bandLo &&
      t <= bandHi
    ) {
      const norm = (t - paletteMin) / palette;
      [r, g, b] = lavaColor(norm);
      a = 1;
    }
    for (let v = 0; v < 3; v++) {
      const i = (f * 3 + v) * 4;
      colors[i] = r;
      colors[i + 1] = g;
      colors[i + 2] = b;
      colors[i + 3] = a;
    }
  }

  return new THREE.BufferAttribute(colors, 4);
}

